// packages/api-client/src/services/admin/adminAgentVersionsService.ts

import type { AgentVersion, UUID, AvatarsBlock } from '@dopomogai/types';
import type { ServiceResponse } from '@dopomogai/types';
import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';

const ADMIN_AGENT_VERSIONS_PREFIX = '/admin/agent_versions';

export interface AdminListVersionsParams {
  agent_id?: string;
  user_id?: UUID;
  limit?: number;
  offset?: number;
}

export interface AdminVersionAvatarGenRequest {
  industry_label: string;
  primary_hex: string;
  company_hint?: string;
}

export const adminAgentVersionsService = {
  /**
   * Admin: Lists all agent versions with optional filters.
   */
  async listAllVersions(params: AdminListVersionsParams = {}): Promise<ServiceResponse<AgentVersion[]>> {
    const queryParams = new URLSearchParams();
    if (params.agent_id) queryParams.set('agent_id', params.agent_id);
    if (params.user_id) queryParams.set('user_id', String(params.user_id));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.offset) queryParams.set('offset', String(params.offset));

    try {
      const response = await getApiClient().get<AgentVersion[]>(`${ADMIN_AGENT_VERSIONS_PREFIX}?${queryParams.toString()}`);
      return response.data;
    } catch (err) {
      return handleApiError(err, 'listing all admin agent versions');
    }
  },

  /**
   * Admin: Retrieves a single agent version by its ID.
   */
  async getAgentVersion(versionId: UUID): Promise<ServiceResponse<AgentVersion>> {
    try {
      const response = await getApiClient().get<AgentVersion>(`${ADMIN_AGENT_VERSIONS_PREFIX}/${versionId}`);
      return response.data;
    } catch (err) {
      return handleApiError(err, `fetching admin agent version '${versionId}'`);
    }
  },

  /**
   * Admin: Deletes an agent version.
   */
  async deleteAgentVersion(versionId: UUID): Promise<ServiceResponse<void>> {
    try {
      const response = await getApiClient().delete<void>(`${ADMIN_AGENT_VERSIONS_PREFIX}/${versionId}`);
      return response.data;
    } catch (err) {
      return handleApiError(err, `deleting admin agent version '${versionId}'`);
    }
  },

  /**
   * Admin: Generates avatars for an agent version.
   */
  async generateAvatars(versionId: UUID, payload: AdminVersionAvatarGenRequest): Promise<ServiceResponse<AvatarsBlock>> {
    try {
      const response = await getApiClient().post<AvatarsBlock>(`${ADMIN_AGENT_VERSIONS_PREFIX}/${versionId}/avatars/generate`, payload);
      return response.data;
    } catch (err) {
      return handleApiError(err, `generating avatars for admin agent version '${versionId}'`);
    }
  },
};